function getOrdinal(day) {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/**
 * Format: "May 11, 2026"  (Iter 69.1.2 — dropped ordinal per global spec)
 */
export function formatTaskDate(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr + (dateStr.length === 10 ? 'T00:00:00' : ''));
    return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  } catch {
    return dateStr;
  }
}

/**
 * Format: "Monday, May 11, 2026"  (Iter 69.1.2 — dropped ordinal)
 */
export function formatHeaderDate(d) {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return `${days[d.getDay()]}, ${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

/**
 * Get current time in a specific timezone
 * Returns: "3:45 PM IST" or "10:15 AM ET"
 */
export function getCurrentTimeInTz(tz) {
  if (!tz) return '';
  try {
    return new Date().toLocaleTimeString('en-US', {
      timeZone: tz,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return '';
  }
}

/**
 * Get timezone abbreviation from IANA timezone
 */
export function getTzAbbr(tz) {
  if (!tz) return '';
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' }).formatToParts(new Date());
    const tzPart = parts.find(p => p.type === 'timeZoneName');
    return tzPart?.value || '';
  } catch {
    return '';
  }
}

/**
 * Format: "3:45 PM ET"
 */
export function formatTimeWithTz(tz) {
  const time = getCurrentTimeInTz(tz);
  const abbr = getTzAbbr(tz);
  if (!time) return '';
  return `${time} ${abbr}`;
}


const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Returns YYYY-MM-DD for the given Date in the BROWSER'S LOCAL timezone.
 * Use this anywhere you need to compare a task's due_date (which is stored as
 * a YYYY-MM-DD string) to "today" without UTC drift.
 */
export function localDateStr(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Given a Date, return [mondayStr, sundayStr] for the calendar week (Mon-Sun)
 * containing that date, in local timezone. Used by the Tasks-page Day/Week/Month
 * time-window filter. Week starts Monday regardless of user locale.
 */
export function weekRangeMonSun(refDate = new Date()) {
  const d = new Date(refDate.getFullYear(), refDate.getMonth(), refDate.getDate());
  // JS getDay(): Sun=0, Mon=1, ..., Sat=6. Convert to Mon=0..Sun=6.
  const dayIndex = (d.getDay() + 6) % 7;
  const monday = new Date(d);
  monday.setDate(d.getDate() - dayIndex);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return [localDateStr(monday), localDateStr(sunday)];
}

/**
 * Return [firstStr, lastStr] for the calendar month containing refDate, local TZ.
 */
export function monthRange(refDate = new Date()) {
  const first = new Date(refDate.getFullYear(), refDate.getMonth(), 1);
  const last = new Date(refDate.getFullYear(), refDate.getMonth() + 1, 0);
  return [localDateStr(first), localDateStr(last)];
}

/**
 * Display-friendly date format:
 * - Today → "Today"
 * - Yesterday → "Yesterday"
 * - Tomorrow → "Tomorrow"
 * - Any other date → "May 11, 2026" (full month, no ordinal, no leading zero on day)
 *
 * IMPORTANT: This is for DISPLAY only. Never parse this output back into a Date.
 * Always keep ISO strings for data/API operations.
 *
 * Iter 69.1.2 — Dhrumil-spec global date format. The legacy short-month
 * shortcuts ("Apr 28") were dropped to match the rest of the product.
 */
export function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    const iso = String(dateStr).length === 10 ? dateStr + 'T00:00:00' : dateStr;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return dateStr;
    const now = new Date();
    const todayStr = localDateStr(now);
    const yesterdayStr = localDateStr(new Date(now.getTime() - 86400000));
    const tomorrowStr = localDateStr(new Date(now.getTime() + 86400000));
    const dateOnly = String(dateStr).slice(0, 10);
    if (dateOnly === todayStr) return 'Today';
    if (dateOnly === yesterdayStr) return 'Yesterday';
    if (dateOnly === tomorrowStr) return 'Tomorrow';
    return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  } catch {
    return dateStr;
  }
}

/**
 * Canonical long date format (no shortcuts) — "May 11, 2026".
 * Use this when you want the literal date with no "Today/Yesterday" smartness,
 * e.g. PDF labels, audit log, account creation rows.
 */
export function formatDateLong(dateStr) {
  if (!dateStr) return '—';
  try {
    const iso = String(dateStr).length === 10 ? dateStr + 'T00:00:00' : dateStr;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  } catch {
    return '—';
  }
}

/**
 * Canonical datetime format — "May 11, 2026, 9:50 AM" in user's local TZ.
 */
export function formatDateTimeLong(value) {
  if (!value) return '—';
  try {
    const s = String(value);
    if (s.length === 10) {
      const d0 = new Date(s + 'T00:00:00');
      if (isNaN(d0.getTime())) return '—';
      return `${MONTHS[d0.getMonth()]} ${d0.getDate()}, ${d0.getFullYear()}`;
    }
    const d = new Date(s);
    if (isNaN(d.getTime())) return '—';
    const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
    return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}, ${time}`;
  } catch {
    return '—';
  }
}

